import React, { useMemo } from 'react'
import { AlertTriangle, Download } from 'lucide-react'
import { format, getDay, eachDayOfInterval } from 'date-fns'
import * as XLSX from 'xlsx'
import { getExamCountForDateNew } from '../utils/examUtils'
import { parseIntSafe } from '../utils/parseUtils'
import { isHoliday, getHolidayName } from '../constants/holidays'

const BenchmarkExceedTable = ({ 
  data = [], 
  getDaysToShow,
  benchmarkData = []
}) => {

  // Define exam categories mapping  
  const examCategoryMapping = {
    'Siêu âm bụng': ['sieuam_bung_sang', 'sieuam_bung_chieu'],
    'Siêu âm vú': ['sieuam_vu_sang', 'sieuam_vu_chieu'],  
    'Siêu âm giáp': ['sieuam_giap_sang', 'sieuam_giap_chieu'],
    'Siêu âm tim': ['sieuam_tim_sang', 'sieuam_tim_chieu'],
    'SA động mạch cảnh': ['sieuam_dong_mach_canh_sang', 'sieuam_dong_mach_canh_chieu'],
    'Điện tâm đồ': ['dien_tam_do_sang', 'dien_tam_do_chieu'],
    'Khám phụ khoa': ['kham_phu_khoa_sang', 'kham_phu_khoa_chieu'],
    'Nội tổng quát': ['so_nguoi_kham'], // Special case: uses total people examined
    'X-Quang': ['x quang sang', 'x quang chieu']
  }

  // Get benchmark limits for each category
  const getBenchmarkLimit = (categoryName) => {
    const benchmarkMapping = {
      'Siêu âm bụng': 'Siêu âm - Bụng',
      'Siêu âm vú': 'Siêu âm - Vú',
      'Siêu âm giáp': 'Siêu âm - Giáp', 
      'Siêu âm tim': 'Siêu âm - Tim',
      'SA động mạch cảnh': 'Siêu âm - Động mạch cảnh',
      'Điện tâm đồ': 'Điện tim (ECG)',
      'Khám phụ khoa': 'Sản phụ khoa',
      'Nội tổng quát': 'Nội tổng quát',
      'X-Quang': 'X-Quang'
    }

    const benchmarkName = benchmarkMapping[categoryName]
    const benchmark = benchmarkData.find(b => b.chuyen_khoa === benchmarkName)
    
    if (!benchmark) return 0
    
    // Return daily limit (average of min and max)
    return Math.round((benchmark.so_ca_ngay_bs_min + benchmark.so_ca_ngay_bs_max) / 2)
  }

  // Calculate room requirements matrix data for table format
  const roomMatrixData = useMemo(() => {
    if (!getDaysToShow) return { days: [], categories: [] }

    const days = getDaysToShow()
    const dateMap = new Map()

    // Create date map for aggregation (same logic as charts)
    days.forEach(dayInfo => {
      const dateKey = format(new Date(dayInfo.date), 'yyyy-MM-dd')
      const dayOfWeek = getDay(new Date(dayInfo.date))
      const dayLabels = ['CN', 'T2', 'T3', 'T4', 'T5', 'T6', 'T7']
      const isHolidayDate = isHoliday(dateKey)
      
      dateMap.set(dateKey, {
        date: dateKey,
        dayOfMonth: new Date(dayInfo.date).getDate(),
        dayLabel: dayLabels[dayOfWeek],
        isHoliday: isHolidayDate,
        holidayName: isHolidayDate ? getHolidayName(dateKey) : null,
        ultrasound: 0,  // Total ultrasound cases
        ecg: 0,         // ECG cases
        gynecology: 0,  // Gynecology cases
        internalMedicine: 0,  // Internal Medicine cases
        xray: 0         // X-Ray cases
      })
    })

    // Process actual data (same logic as charts)
    data.forEach(item => {
      // Use the new unified logic for all date calculations
      // daysToShow.forEach(date => {
      days.forEach(dayInfo => {
        const date = new Date(dayInfo.date)
        const examResult = getExamCountForDateNew(item, date)
        if (examResult.total > 0) {
          const dateKey = format(date, 'yyyy-MM-dd')
          const dayData = dateMap.get(dateKey)
          
          // Skip processing data for holidays (keep them at 0)
          if (dayData && !dayData.isHoliday) {
            // ECG
            const ecgMorning = parseIntSafe(item['dien tam do sang'], examResult.morning)
            const ecgAfternoon = parseIntSafe(item['dien tam do chieu'], examResult.afternoon)
            dayData.ecg += ecgMorning + ecgAfternoon
            
            // Gynecology
            const gynecoMorning = parseIntSafe(item['kham phu khoa sang'], examResult.morning)
            const gynecoAfternoon = parseIntSafe(item['kham phu khoa chieu'], examResult.afternoon)
            dayData.gynecology += gynecoMorning + gynecoAfternoon
            
            // Internal Medicine (using new unified logic)
            dayData.internalMedicine += examResult.total
            
            // X-Ray
            const xrayMorning = parseIntSafe(item['x quang sang'], examResult.morning)
            const xrayAfternoon = parseIntSafe(item['x quang chieu'], examResult.afternoon)
            dayData.xray += xrayMorning + xrayAfternoon
          
            // Ultrasound total (all categories)
            const ultrasoundMappings = [
              ['sieu am bung sang', 'sieu am bung chieu'],
              ['sieu am vu sang', 'sieu am vu chieu'],
              ['sieu am giap sang', 'sieu am giap chieu'],
              ['sieu am tim sang', 'sieu am tim chieu'],
              ['sieu am dong mach canh sang', 'sieu am dong mach canh chieu']
            ]
            
            ultrasoundMappings.forEach(fields => {
              const morning = parseIntSafe(item[fields[0]], examResult.morning)
              const afternoon = parseIntSafe(item[fields[1]], examResult.afternoon)
              dayData.ultrasound += morning + afternoon
            })
          }
        }
      })
    })

    // Calculate required rooms for each category and day using dynamic benchmarks
    const getRequiredRooms = (totalCases, category) => {
      if (totalCases === 0) return 0
      
      let dailyCapacity = 90 // default fallback
      let maxRooms = null // no limit by default
      
      if (category === 'ultrasound') {
        // Use siêu âm bụng benchmark (110 ca/ngày), max 3 phòng
        const ultrasoundBenchmark = getBenchmarkLimit('Siêu âm bụng')
        dailyCapacity = ultrasoundBenchmark || 110
        maxRooms = 3
      } else if (category === 'internalMedicine') {
        // Use nội tổng quát benchmark (90 ca/ngày)
        const internalBenchmark = getBenchmarkLimit('Nội tổng quát')
        dailyCapacity = internalBenchmark || 90
      } else if (category === 'ecg') {
        // Use điện tâm đồ benchmark (100 ca/ngày)
        const ecgBenchmark = getBenchmarkLimit('Điện tâm đồ')
        dailyCapacity = ecgBenchmark || 100
      } else if (category === 'gynecology') {
        // Use khám phụ khoa benchmark (40 ca/ngày)
        const gynecoBenchmark = getBenchmarkLimit('Khám phụ khoa')
        dailyCapacity = gynecoBenchmark || 40
      } else if (category === 'xray') {
        // Use X-Quang benchmark (100 ca/ngày), max 3 phòng
        const xrayBenchmark = getBenchmarkLimit('X-Quang')
        dailyCapacity = xrayBenchmark || 100
        maxRooms = 3
      }
      
      const calculatedRooms = Math.ceil(totalCases / dailyCapacity)
      return maxRooms ? Math.min(calculatedRooms, maxRooms) : calculatedRooms
    }

    // Prepare sorted days data
    const sortedDays = Array.from(dateMap.values()).sort((a, b) => 
      new Date(a.date) - new Date(b.date)
    )

    // Prepare categories with room requirements for each day
    const categories = [
      {
        name: 'Siêu âm',
        key: 'ultrasound',
        rooms: sortedDays.map(day => getRequiredRooms(day.ultrasound, 'ultrasound'))
      },
      {
        name: 'Nội tổng quát',
        key: 'internalMedicine',
        rooms: sortedDays.map(day => getRequiredRooms(day.internalMedicine, 'internalMedicine'))
      },
      {
        name: 'Điện tim',
        key: 'ecg',
        rooms: sortedDays.map(day => getRequiredRooms(day.ecg, 'ecg'))
      },
      {
        name: 'Phụ khoa',
        key: 'gynecology',
        rooms: sortedDays.map(day => getRequiredRooms(day.gynecology, 'gynecology'))
      },
      {
        name: 'X-Quang',
        key: 'xray',
        rooms: sortedDays.map(day => getRequiredRooms(day.xray, 'xray'))
      }
    ]

    return { days: sortedDays, categories }
  }, [data, getDaysToShow, benchmarkData])

  // Export to Excel function
  const exportToExcel = () => {
    const { days, categories } = roomMatrixData
    
    // Create header row with days
    const header = ['Hạng mục', ...days.map(day => day.dayOfMonth)]
    
    // Create data rows for each category
    const exportData = [header]
    categories.forEach(category => {
      const row = [category.name, ...category.rooms.map(rooms => rooms || '')]
      exportData.push(row)
    })

    const ws = XLSX.utils.aoa_to_sheet(exportData)
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'Số phòng cần thiết')
    XLSX.writeFile(wb, `so-phong-can-thiet-${new Date().toISOString().split('T')[0]}.xlsx`)
  }

  // Helper function to get cell styling based on room count and date
  const getRoomCellStyle = (roomCount, dayDate) => {
    const today = new Date()
    const currentDate = new Date(dayDate)
    const isPastDate = currentDate < today && currentDate.toDateString() !== today.toDateString()
    
    if (roomCount === 0 || !roomCount) return 'text-gray-300'
    
    // If it's a past date, use muted colors
    if (isPastDate) {
      return 'text-gray-400 font-normal'
    }
    
    // Current and future dates use original logic
    if (roomCount === 1) return 'text-gray-800 font-normal'
    if (roomCount === 2) return 'text-blue-600 font-normal'
    if (roomCount >= 3) return 'text-red-600 font-bold'
    return 'text-gray-800'
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-semibold text-gray-900">Phòng cần thiết</h2>
        
        {roomMatrixData.days.length > 0 && (
          <button
            onClick={exportToExcel}
            className="flex items-center space-x-2 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded transition-colors duration-200"
          >
            <Download className="w-4 h-4" />
            <span>Xuất Excel</span>
          </button>
        )}
      </div>

      {roomMatrixData.days.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <p>Không có dữ liệu trong khoảng thời gian này</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white">
            <thead>
              <tr className="bg-white">
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 border-b border-gray-200 w-32">
                  Hạng mục
                </th>
                {roomMatrixData.days.map((day, index) => {
                  const today = new Date()
                  const isToday = new Date(day.date).toDateString() === today.toDateString()
                  const isHolidayDate = day.isHoliday
                  
                  return (
                    <th key={index} className={`px-2 py-3 text-center text-sm font-medium text-gray-700 border-b border-gray-200 min-w-[40px] ${isToday ? 'bg-gray-100' : ''}`} style={{ backgroundColor: isHolidayDate ? '#fefcfb' : '' }}>
                      <div className="flex flex-col items-center">
                        <span className="text-xs text-gray-500">{day.dayLabel}</span>
                        <span className="font-normal">{day.dayOfMonth}</span>
                      </div>
                    </th>
                  )
                })}
              </tr>
            </thead>
            <tbody>
              {roomMatrixData.categories.map((category, categoryIndex) => (
                <tr key={category.key} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm font-medium text-gray-900 bg-white">
                    {category.name}
                  </td>
                  {category.rooms.map((roomCount, dayIndex) => {
                    const day = roomMatrixData.days[dayIndex]
                    const today = new Date()
                    const isToday = new Date(day.date).toDateString() === today.toDateString()
                    const isHolidayDate = day.isHoliday
                    
                    return (
                      <td key={dayIndex} className={`px-2 py-3 text-center text-sm ${isToday ? 'bg-gray-100' : ''}`} style={{ backgroundColor: isHolidayDate ? '#fefcfb' : '' }}>
                        <span className={getRoomCellStyle(roomCount, day.date)}>
                          {roomCount || ''}
                        </span>
                      </td>
                    )
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

export default BenchmarkExceedTable
